import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import { useAuth } from "../context/AuthContext";

import TopNav from "../components/navigation/TopNav";
import StandardScreen, {
  ScreenContent,
} from "../components/layout/StandardScreen";
import Stack from "../components/layout/Stack";
import Card from "../components/ui/Card";
import FormField from "../components/forms/FormField";
import {
  TableShell,
  Table,
  Th,
  Td,
  NumberTd,
} from "../components/data/Table";

const narrowShellStyle = {
  width: "100%",
  maxWidth: "960px",
  margin: "0 auto",
};

const errorTextStyle = {
  color: "var(--color-danger)",
};

const disabledInputStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "0 12px",
  border: "1px solid var(--color-border, #d0d7de)",
  borderRadius: "8px",
  backgroundColor: "var(--color-surface-muted, #f6f8fa)",
  color: "var(--color-text, inherit)",
  fontSize: "var(--font-size-md)",
  opacity: 1,
};

const BillsSettlementPlan = () => {
  const { t } = useTranslation();
  const { user, loadingUser } = useAuth();

  const [excursions, setExcursions] = useState([]);
  const [selectedExcursion, setSelectedExcursion] = useState("");

  const [members, setMembers] = useState([]);
  const [totals, setTotals] = useState({});
  const [settlement, setSettlement] = useState([]);

  const [loadingExcursions, setLoadingExcursions] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [excursionsError, setExcursionsError] = useState("");
  const [reportError, setReportError] = useState("");

  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchExcursions = async () => {
      setLoadingExcursions(true);
      setExcursionsError("");

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/excursions/i-can-see`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = Array.isArray(res.data) ? res.data : [];
        setExcursions(data);

        if (user?.excursion?.excursion_id) {
          const activeId = String(user.excursion.excursion_id);
          const existsInList = data.some(
            (exc) => String(exc.excursion_id) === activeId
          );

          if (existsInList) {
            setSelectedExcursion(activeId);
          }
        }
      } catch (err) {
        console.error("Failed to fetch excursions", err);
        setExcursionsError(t("settlement.errors.load_excursions"));
      } finally {
        setLoadingExcursions(false);
      }
    };

    if (token && user) {
      fetchExcursions();
    }
  }, [token, user, t]);

  useEffect(() => {
    if (!selectedExcursion || !token) {
      setMembers([]);
      setTotals({});
      setSettlement([]);
      setReportError("");
      return;
    }

    const fetchSettlement = async () => {
      setLoadingReport(true);
      setReportError("");

      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/bills/settlement/${selectedExcursion}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        setMembers(res.data?.members || []);
        setTotals(res.data?.totals || {});
        setSettlement(res.data?.settlement || []);
      } catch (err) {
        console.error("Failed to fetch settlement plan", err);
        setMembers([]);
        setTotals({});
        setSettlement([]);
        setReportError(t("settlement.errors.load_report"));
      } finally {
        setLoadingReport(false);
      }
    };

    fetchSettlement();
  }, [selectedExcursion, token, t]);

  if (loadingUser) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settlement.title")} mode="main" />}
        isLoading
      />
    );
  }

  if (!user) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settlement.title")} mode="main" />}
        isError
        errorState={
          <div style={errorTextStyle}>
            {t("settlement.errors.no_session")}
          </div>
        }
      />
    );
  }

  const allowedStatuses = ["approved", "pending", "suspended"];
  const isEligible =
    user.role === "admin" ||
    allowedStatuses.includes(String(user.subscription_status || ""));

  if (!isEligible) {
    return (
      <StandardScreen
        topNav={<TopNav title={t("settlement.title")} mode="main" />}
      >
        <ScreenContent>
          <Card variant="accent">
            <strong>{t("settlement.errors.no_access")}</strong>
          </Card>
        </ScreenContent>
      </StandardScreen>
    );
  }

  const selectedExcursionData = excursions.find(
    (exc) => String(exc.excursion_id) === String(selectedExcursion)
  );

  const selectedExcursionLabel = selectedExcursionData
    ? `${selectedExcursionData.short_name} (${selectedExcursionData.start_date} → ${selectedExcursionData.end_date})`
    : loadingExcursions
    ? t("settlement.loading_excursions")
    : t("settlement.no_excursion");

  const memberMap = useMemo(() => {
    const m = {};
    members.forEach((x) => {
      m[x.user_id] = `${x.name} ${x.surname}`;
    });
    return m;
  }, [members]);

  const rows = settlement.map((row) => ({
    from: memberMap[row.from_user_id] || row.from_user_id,
    to: memberMap[row.to_user_id] || row.to_user_id,
    amount:
      typeof row.amount === "number"
        ? row.amount.toFixed(2)
        : row.amount ?? "—",
  }));

  return (
    <StandardScreen topNav={<TopNav title={t("settlement.title")} mode="main" />}>
      <ScreenContent>
        <div style={narrowShellStyle}>
          <Stack gap="5">

            <Card title={t("settlement.sections.excursion")}>
              <FormField>
                <input
                  type="text"
                  value={selectedExcursionLabel}
                  disabled
                  readOnly
                  style={disabledInputStyle}
                />
              </FormField>

              {excursionsError && (
                <div style={errorTextStyle}>{excursionsError}</div>
              )}
            </Card>

            {selectedExcursion && loadingReport && (
              <Card
                title={t("settlement.sections.title")}
                subtitle={t("settlement.sections.subtitle")}
              >
                <div>{t("settlement.loading_report")}</div>
              </Card>
            )}

            {reportError && (
              <Card
                title={t("settlement.sections.title")}
                subtitle={t("settlement.sections.subtitle")}
              >
                <div style={errorTextStyle}>{reportError}</div>
              </Card>
            )}

            {selectedExcursion &&
            !loadingReport &&
            !reportError &&
            settlement.length > 0 && (
              <Card
                title={t("settlement.sections.title")}
                subtitle={t("settlement.sections.subtitle")}
              >
                <TableShell>
                  <Table>
                    <thead>
                      <tr>
                        <Th columnIndex={0} sticky>
                          {t("settlement.table.from")}
                        </Th>
                        <Th>{t("settlement.table.to")}</Th>
                        <Th align="right">
                          {t("settlement.table.amount")}
                        </Th>
                      </tr>
                    </thead>

                    <tbody>
                      {rows.map((row, index) => (
                        <tr key={`${row.from}-${row.to}-${index}`}>
                          <Td columnIndex={0} sticky>
                            {row.from ?? "—"}
                          </Td>

                          <Td>{row.to ?? "—"}</Td>

                          <NumberTd>{row.amount ?? "—"}</NumberTd>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableShell>
              </Card>
            )}

            {selectedExcursion &&
            !loadingReport &&
            !reportError &&
            settlement.length === 0 && (
              <Card title={t("settlement.title")}>
                <div>{t("settlement.messages.no_data")}</div>
              </Card>
            )}

          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
};

export default BillsSettlementPlan;